import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Upload, Copy, Plus, ArrowUp, RotateCw, FileText, X, FileUp, Coins, Loader2, AlertCircle, Check } from "lucide-react";
import artieImage from "../../assets/images/artie.png";
import mattImage from "../../assets/images/matt.png";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiClient } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { ChatMessage, ArtieScoreData, Recommendation, TacticRecommendation } from "@/types/artie-score";
import { useToast } from "@/hooks/use-toast";

interface Message {
    id: string;
    sender: 'user' | 'ai';
    content: React.ReactNode;
    timestamp: string;
}

interface ScoreStatusResponse {
    score_data: ArtieScoreData;
}

const ChatbotPage: React.FC = () => {
    const navigate = useNavigate();
    const { projectId } = useParams<{ projectId: string }>();
    const location = useLocation();
    const initialRecommendation = location.state?.recommendation;
    const { toast } = useToast();

    const [inputValue, setInputValue] = useState("");
    const [workingPlan, setWorkingPlan] = useState("");
    const [tokensUsed, setTokensUsed] = useState(0);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [showCompletionModal, setShowCompletionModal] = useState(true);
    const [completionMethod, setCompletionMethod] = useState<'upload' | 'chat'>('chat');

    // Chat state
    const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
    const [isLoadingChat, setIsLoadingChat] = useState(false);
    const [chatError, setChatError] = useState<string | null>(null);
    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [hasEngaged, setHasEngaged] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const queryClient = useQueryClient();

    const TOKEN_LIMIT = 10000;
    const tokenPercentage = (tokensUsed / TOKEN_LIMIT) * 100;

    // Fetch score status to get recommendations if not passed in state
    const { data: scoreStatus } = useQuery<ScoreStatusResponse>({
        queryKey: ['artieScoreStatus', projectId],
        queryFn: () => apiClient.getArtieScoreStatus(projectId!) as Promise<ScoreStatusResponse>,
        enabled: !!projectId && !initialRecommendation,
    });

    // Use V2 tactics first, fallback to legacy recommendations
    const currentRecommendations = scoreStatus?.score_data?.current_recommendations || [];
    const legacyRecommendations = scoreStatus?.score_data?.recommendations || [];
    const allRecommendations = (currentRecommendations.length ? currentRecommendations : legacyRecommendations).filter(rec => !rec.is_hidden);

    const recommendation = initialRecommendation ||
        (allRecommendations?.find(r => r.status === 'generated' || r.status === 'in_progress') || allRecommendations?.[0]);

    // Helper functions for V2/legacy compatibility
    const getRecommendationId = (rec: Recommendation | TacticRecommendation) => {
        const isV2Tactic = 'tactic_id' in rec;
        return isV2Tactic ? (rec as TacticRecommendation).tactic_id : (rec.id || (rec as Recommendation).what_to_improve);
    };

    const getRecommendationName = (rec: Recommendation | TacticRecommendation) => {
        const isV2Tactic = 'tactic_id' in rec;
        return isV2Tactic
            ? (rec as TacticRecommendation).tactic_name
            : (rec as Recommendation).what_to_improve;
    };

    const isV2Tactic = recommendation && 'tactic_id' in recommendation;

    // Fetch chat history for V2 tactics
    const { data: chatHistoryData } = useQuery({
        queryKey: ['tacticChatHistory', projectId, recommendation ? getRecommendationId(recommendation) : null],
        queryFn: () => {
            if (!projectId || !recommendation || !isV2Tactic) return null;
            const tacticId = getRecommendationId(recommendation);
            return apiClient.getTacticChatHistory(projectId, tacticId);
        },
        enabled: !!projectId && !!recommendation && isV2Tactic,
    });

    // Initialize state from recommendation or fetched chat history
    useEffect(() => {
        if (recommendation) {
            if (isV2Tactic && chatHistoryData) {
                // Use fetched chat history for V2 tactics
                const messages = chatHistoryData.chat_messages || [];
                setChatHistory(messages as ChatMessage[]);
                setTokensUsed(chatHistoryData.token_usage || 0);
            } else if (!isV2Tactic) {
                // Use recommendation data for legacy recommendations
                if (recommendation.chat_messages) {
                    setChatHistory(recommendation.chat_messages);
                }
                if (recommendation.token_usage) {
                    setTokensUsed(recommendation.token_usage);
                }
            }
        }
    }, [recommendation, chatHistoryData, isV2Tactic]);

    // Combined messages for display (preserving the original UI structure)
    const [displayMessages, setDisplayMessages] = useState<Message[]>([]);

    // Helper function to check if a message is a final implementation plan
    const isFinalImplementationPlan = (content: string) => {
        return content.includes("Perfect! Based on what you've shared") ||
            content.includes("Your Customized Strategy") ||
            content.includes("Key Actions for You") ||
            content.includes("Next Steps:");
    };

    useEffect(() => {
        const formattedMessages: Message[] = chatHistory.map((msg, idx) => ({
            id: idx.toString(),
            sender: msg.role === 'user' ? 'user' : 'ai',
            timestamp: new Date(msg.timestamp).toLocaleTimeString(),
            content: msg.role === 'user' ? (
                msg.content
            ) : (
                <div className="space-y-4 w-full">
                    <div className="bg-transparent text-gray-800 text-sm leading-relaxed">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                    <div className="flex gap-2 mt-2">
                        {/* Only show "Add to plan" button for final implementation plans */}
                        {isFinalImplementationPlan(msg.content) && (
                            <Button
                                variant="secondary"
                                className="h-8 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium"
                                onClick={() => setWorkingPlan(prev => prev + (prev ? "\n\n" : "") + msg.content)}
                            >
                                <Plus className="w-3 h-3 mr-1.5" /> Add to plan
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            className="h-8 text-xs text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                            onClick={() => handleCopy(msg.content, idx)}
                        >
                            {copiedIndex === idx ? <Check className="w-3 h-3 mr-1.5" /> : <Copy className="w-3 h-3 mr-1.5" />}
                            {copiedIndex === idx ? "Copied" : "Copy"}
                        </Button>
                    </div>
                </div>
            )
        }));

        if (chatHistory.length === 0) {
            // Initial welcome message if no history
            setDisplayMessages([{
                id: '1',
                sender: 'ai',
                timestamp: 'Just now',
                content: (
                    <div>
                        <div className="bg-transparent text-gray-800 text-sm leading-relaxed mb-3">
                            Hi! I'm here to help you working through {recommendation ? `"${getRecommendationName(recommendation)}"` : "your project tasks"}. We can explore different channels, think through timelines, or I can help you draft a complete plan. What would be most helpful?
                        </div>
                        <div className="flex flex-col gap-2 items-start">
                            <Button
                                variant="outline"
                                className="rounded-full bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900 text-xs h-auto py-2 px-4 font-normal text-left shadow-sm"
                                onClick={() => handleSendMessage("Can you give me a step-by-step implementation plan?")}
                            >
                                Can you give me a step-by-step implementation plan?
                            </Button>
                        </div>
                    </div>
                )
            }]);
        } else {
            setDisplayMessages(formattedMessages);
        }
    }, [chatHistory, copiedIndex, recommendation]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [displayMessages]);

    const handleSendMessage = async (text?: string) => {
        const contentToSend = text || inputValue;
        if (!contentToSend.trim() || isLoadingChat || !projectId || !recommendation) return;

        setInputValue("");
        setChatError(null);
        setIsLoadingChat(true);

        // Mark as engaged and update status to in_progress on first message
        if (!hasEngaged && recommendation.status !== 'in_progress') {
            setHasEngaged(true);
            const recId = getRecommendationId(recommendation);
            try {
                if (isV2Tactic) {
                    await apiClient.updateTacticRecommendation(projectId, recId, {
                        recommendation_id: recId,
                        status: 'in_progress',
                    });
                } else {
                    await apiClient.updateRecommendation(projectId, {
                        recommendation_id: recId,
                        status: 'in_progress',
                    });
                }
                await queryClient.invalidateQueries({ queryKey: ['artieScoreStatus', projectId] });
            } catch (error) {
                console.error("Failed to update task status:", error);
            }
        }

        // Optimistically add user message for UI responsiveness
        const optimisticMsg: ChatMessage = {
            role: "user",
            content: contentToSend,
            timestamp: new Date().toISOString()
        };
        setChatHistory(prev => [...prev, optimisticMsg]);

        try {
            const recId = getRecommendationId(recommendation);

            const response = isV2Tactic
                ? await apiClient.goDeeperTacticChat(projectId, {
                    recommendation_id: recId,
                    message: contentToSend,
                })
                : await apiClient.goDeeperChat(projectId, {
                    recommendation_id: recId,
                    message: contentToSend,
                });

            const assistantMessage: ChatMessage = {
                role: "assistant",
                content: response.response,
                timestamp: new Date().toISOString(),
            };

            setChatHistory(prev => [...prev, assistantMessage]);
            setTokensUsed(response.total_token_usage);

            // Invalidate both score status and chat history queries
            queryClient.invalidateQueries({ queryKey: ['artieScoreStatus', projectId] });
            if (isV2Tactic) {
                queryClient.invalidateQueries({ queryKey: ['tacticChatHistory', projectId, getRecommendationId(recommendation)] });
            }

        } catch (err: any) {
            console.error("Chat error:", err);
            setChatError(err.message || "Failed to send message. Please try again.");
        } finally {
            setIsLoadingChat(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSendMessage();
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setSelectedFile(e.dataTransfer.files[0]);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleContinue = () => {
        setShowCompletionModal(false);
        if (completionMethod === 'upload') {
            // If no file is selected, trigger file input
            if (!selectedFile) {
                setTimeout(() => {
                    triggerFileInput();
                }, 300);
            }
            // If file is already selected, proceed with the uploaded file
            // The file upload area will be shown in the main interface
        }
    };

    const handleCopy = (content: string, index: number) => {
        navigator.clipboard.writeText(content);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const handleSaveDraft = async () => {
        if (!projectId || !recommendation) return;

        try {
            setIsSavingDraft(true);
            const recId = getRecommendationId(recommendation);

            // Mark as engaged on save
            if (!hasEngaged) {
                setHasEngaged(true);
            }

            console.log("🐛 [DEBUG] Save draft - Setting status to 'accepted' for 'Saved for later' tab:", {
                projectId,
                recommendationId: recId,
                currentStatus: recommendation.status,
                newStatus: 'accepted',
                hasWorkingPlan: !!workingPlan.trim(),
                hasSelectedFile: !!selectedFile
            });

            // Upload artifact if file is provided
            let artifactUrls: string[] | undefined = undefined;
            if (selectedFile) {
                try {
                    const uploadResult = await apiClient.uploadRecommendationArtifact(projectId, recId, selectedFile);
                    artifactUrls = [uploadResult.url];
                    console.log("🐛 [DEBUG] Draft artifact uploaded successfully:", uploadResult);
                } catch (uploadError) {
                    console.error("Failed to upload draft artifact:", uploadError);
                    // Continue with save even if upload fails
                }
            }

            // Save the current working plan and update status to 'accepted' (for "Saved for later" tab)
            if (isV2Tactic) {
                await apiClient.updateTacticRecommendation(projectId, recId, {
                    recommendation_id: recId,
                    status: 'accepted',
                    plan_description: workingPlan.trim() || undefined,
                    // Note: artifact_urls might not be supported in the type, but the backend handles it
                    ...(artifactUrls && { artifact_urls: artifactUrls }),
                } as any);
            } else {
                await apiClient.updateRecommendation(projectId, {
                    recommendation_id: recId,
                    status: 'accepted',
                    plan_description: workingPlan.trim() || undefined,
                    // Note: artifact_urls might not be supported in the type, but the backend handles it
                    ...(artifactUrls && { artifact_urls: artifactUrls }),
                } as any);
            }

            await queryClient.invalidateQueries({ queryKey: ['artieScoreStatus', projectId] });

            toast({
                title: "Draft saved",
                description: "Your work has been saved for later.",
            });
        } catch (error: any) {
            console.error("Failed to save draft:", error);
            toast({
                title: "Error",
                description: error.message || "Failed to save draft. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsSavingDraft(false);
        }
    };

    const handleMarkComplete = async () => {
        if (!projectId || !recommendation) return;

        // Clear any previous validation errors
        setValidationError(null);

        try {
            setIsLoadingChat(true);

            // Determine completion method based on what user has provided
            const hasFile = selectedFile !== null;
            const hasPlan = workingPlan.trim().length > 0;

            if (!hasFile && !hasPlan) {
                setValidationError("Not quite yet. You'll need to create a plan or upload a document with Artie to finish this task.");
                setIsLoadingChat(false);
                return;
            }

            const completionMethod = hasFile ? 'artifact' : 'plan';
            const recId = getRecommendationId(recommendation);

            // Upload artifact if file is provided
            let artifactUrls: string[] | undefined = undefined;
            if (hasFile && selectedFile) {
                try {
                    const uploadResult = await apiClient.uploadRecommendationArtifact(projectId, recId, selectedFile);
                    artifactUrls = [uploadResult.url];
                    console.log("🐛 [DEBUG] Artifact uploaded successfully:", uploadResult);
                } catch (uploadError) {
                    console.error("Failed to upload artifact:", uploadError);
                    toast({
                        title: "Upload Error",
                        description: "Failed to upload artifact. Please try again.",
                        variant: "destructive",
                    });
                    setIsLoadingChat(false);
                    return;
                }
            }

            // Update recommendation status to 'done'
            if (isV2Tactic) {
                await apiClient.updateTacticRecommendation(projectId, recId, {
                    recommendation_id: recId,
                    status: 'done',
                    completion_method: completionMethod,
                    plan_description: hasPlan ? workingPlan : undefined,
                    // Note: artifact_urls might not be supported in the type, but the backend handles it
                    ...(artifactUrls && { artifact_urls: artifactUrls }),
                } as any);
            } else {
                await apiClient.updateRecommendation(projectId, {
                    recommendation_id: recId,
                    status: 'done',
                    completion_method: completionMethod,
                    plan_description: hasPlan ? workingPlan : undefined,
                    // Note: artifact_urls might not be supported in the type, but the backend handles it
                    ...(artifactUrls && { artifact_urls: artifactUrls }),
                } as any);
            }

            // Apply the score bonus
            await apiClient.applyRecommendationBonus(projectId);

            // Invalidate queries to refresh data
            await queryClient.invalidateQueries({ queryKey: ['artieScoreStatus', projectId] });

            console.log("🐛 [DEBUG] Task marked complete - Navigating with completion state:", {
                projectId,
                taskId: recId,
                completionMethod,
                hasFile,
                hasPlan,
                artifactUrls,
                timestamp: new Date().toISOString()
            });

            // Navigate to project details with success state
            navigate(`/project-v2/project-details-v2/${projectId}`, {
                state: { taskCompleted: true, taskId: recId }
            });

        } catch (error: any) {
            console.error("Failed to mark task complete:", error);
            toast({
                title: "Error",
                description: error.message || "Failed to mark task as complete. Please try again.",
                variant: "destructive",
            });
            setIsLoadingChat(false);
        }
    };

    return (
        <div className="h-screen bg-gray-50 flex flex-col font-sans text-slate-900 overflow-hidden">
            <Dialog open={showCompletionModal} onOpenChange={setShowCompletionModal}>
                <DialogContent className="sm:max-w-[600px] p-0 gap-0 overflow-hidden rounded-3xl border-0 shadow-2xl">
                    <div className="p-8 pb-6">
                        <div className="flex justify-between items-start mb-2">
                            <DialogTitle className="text-2xl font-bold text-gray-900">How do you want to complete this?</DialogTitle>
                        </div>
                        <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                            You can finish this task in one of two ways. If you already have work, upload it. If not, Artie can help you build it from scratch.
                        </p>

                        <div className="space-y-4">
                            {/* Option 1: Upload */}
                            <div
                                className={`border rounded-xl p-6 cursor-pointer transition-all relative ${completionMethod === 'upload'
                                    ? 'border-gray-900 ring-1 ring-gray-900 bg-gray-50/50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                                onClick={() => setCompletionMethod('upload')}
                            >
                                <div className="flex items-center gap-4 mb-2">
                                    <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center flex-shrink-0 text-white">
                                        <FileUp className="w-5 h-5" />
                                    </div>
                                    <h3 className="font-bold text-gray-900">I already have an artifact</h3>
                                    <span className="bg-gray-100 text-gray-600 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full tracking-wide">Strongest confidence signal</span>
                                </div>
                                <p className="text-gray-500 text-sm">Upload a document, deck, or file to complete this task.</p>

                                {completionMethod === 'upload' && (
                                    <div
                                        className="mt-4 border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center text-center bg-white hover:bg-gray-50 transition-colors"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            triggerFileInput();
                                        }}
                                        onDrop={(e) => {
                                            e.stopPropagation();
                                            handleDrop(e);
                                        }}
                                        onDragOver={(e) => {
                                            e.stopPropagation();
                                            handleDragOver(e);
                                        }}
                                    >
                                        {!selectedFile ? (
                                            <>
                                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                                                    <Upload className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <h3 className="text-sm font-bold text-gray-900 mb-1">Drop your file here or click to browse</h3>
                                                <p className="text-xs text-gray-400">Accepted formats: PDF, DOC, DOCX, PPT, PPTX, PNG, JPG</p>
                                            </>
                                        ) : (
                                            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                                                <FileText className="w-4 h-4 text-gray-500" />
                                                <span className="text-sm text-gray-700">{selectedFile.name}</span>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedFile(null);
                                                    }}
                                                >
                                                    <X className="w-3 h-3 text-gray-500" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Option 2: Chat */}
                            <div
                                className={`border rounded-xl p-6 cursor-pointer transition-all relative ${completionMethod === 'chat'
                                    ? 'border-gray-900 ring-1 ring-gray-900 bg-gray-50/50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                                onClick={() => setCompletionMethod('chat')}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-4">
                                        <img src={artieImage} alt="Artie" className="w-10 h-10 rounded-full object-contain bg-yellow-50 flex-shrink-0" />
                                        <h3 className="font-bold text-gray-900">Work with Artie</h3>
                                        <span className="bg-gray-100 text-gray-600 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full tracking-wide">Guided by brand insights</span>
                                    </div>
                                </div>

                                <div className="flex justify-between items-start">
                                    <p className="text-gray-500 text-sm max-w-[95%] mb-6">Work with Artie to create a plan or artifact aligned with brand expectations and your current score.</p>
                                </div>

                                {completionMethod === 'chat' && (
                                    <div className="pt-6 border-t border-gray-400">
                                        <div className="flex gap-3 mb-3">
                                            <Coins className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                                            <p className="text-sm text-gray-600 leading-relaxed">
                                                Artie runs on tokens, so deeper collaboration uses more of them. Think of tokens like creative fuel. Spend them where it counts.
                                            </p>
                                        </div>

                                        <div className="space-y-1.5">
                                            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-purple-600 rounded-full"
                                                    style={{ width: `${Math.min(tokenPercentage, 100)}%` }}
                                                ></div>
                                            </div>
                                            <div className="flex justify-between text-xs font-bold text-gray-600">
                                                <span>{tokensUsed.toLocaleString()} <span className="text-gray-400 font-normal">/ {TOKEN_LIMIT.toLocaleString()}</span></span>
                                                <span className="text-gray-600">{Math.max(0, 100 - tokenPercentage).toFixed(0)}% remaining</span>
                                            </div>
                                        </div>

                                        <p className="text-xs text-gray-500 mt-3">
                                            More complex plans use more tokens, but usually lead to better outcomes.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                        <Button
                            variant="ghost"
                            className="text-gray-500 hover:text-gray-900 font-medium"
                            onClick={() => setShowCompletionModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            className={`${completionMethod ? 'bg-black hover:bg-gray-800' : 'bg-[#A8A8A8] hover:bg-gray-600'} text-white font-medium px-8 rounded-md transition-colors`}
                            onClick={handleContinue}
                        >
                            Continue →
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Header */}
            <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shrink-0 z-10">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-900">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 leading-tight">
                            {recommendation ? getRecommendationName(recommendation) : "Chat with Artie"}
                        </h1>
                        <p className="text-sm text-gray-500">Task Execution</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        className="text-gray-500 hover:text-gray-900 font-medium"
                        onClick={handleSaveDraft}
                        disabled={isSavingDraft || isLoadingChat}
                    >
                        {isSavingDraft ? "Saving..." : "Save draft"}
                    </Button>
                    <Button
                        className="bg-black text-white hover:bg-gray-800 rounded-md px-4 py-2 text-sm font-medium"
                        onClick={handleMarkComplete}
                        disabled={isLoadingChat}
                    >
                        Mark task complete
                    </Button>
                </div>
            </header>

            {/* Validation Error Banner */}
            {validationError && (
                <div className="max-w-[1600px] w-full mx-auto px-6 pt-4">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm text-red-800 font-medium">{validationError}</p>
                        </div>
                        <button
                            onClick={() => setValidationError(null)}
                            className="text-red-400 hover:text-red-600 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            <main className="flex-1 max-w-[1600px] w-full mx-auto p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0">

                {/* Left Sidebar - Working Plan */}
                <div className="lg:col-span-4 bg-white rounded-2xl border border-gray-200 p-6 flex flex-col h-full overflow-hidden shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="font-bold text-gray-900">Your working plan</h2>
                        <Button
                            variant="ghost"
                            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 text-sm font-medium h-auto py-1.5 px-3"
                            onClick={() => setWorkingPlan("")}
                        >
                            <RotateCw className="w-3.5 h-3.5 mr-1.5" /> Clear
                        </Button>
                    </div>

                    <p className="text-sm text-gray-500 mb-4">
                        As you chat with Artie, you can save ideas here and turn them into a plan.
                    </p>

                    <div className="flex-1 mb-4 relative">
                        <Textarea
                            className="w-full h-full resize-none border-gray-100 bg-gray-50/50 rounded-xl p-4 text-sm focus-visible:ring-1 focus-visible:ring-gray-200"
                            placeholder="Distribution strategy..."
                            value={workingPlan}
                            onChange={(e) => setWorkingPlan(e.target.value)}
                        />
                    </div>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-gray-100" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-white px-2 text-gray-400">Or upload an existing document</span>
                        </div>
                    </div>

                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={handleFileSelect}
                        accept=".pdf,.doc,.docx,.ppt,.pptx,.png,.jpg"
                    />

                    {selectedFile ? (
                        <div className="mt-4 border border-gray-200 rounded-xl p-4 flex items-center justify-between bg-white shadow-sm">
                            <div className="flex items-center gap-3 overflow-hidden">
                                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-5 h-5 text-purple-600" />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="text-sm font-bold text-gray-900 truncate">{selectedFile.name}</h3>
                                    <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => setSelectedFile(null)}
                            >
                                Change
                            </Button>
                        </div>
                    ) : (
                        <div
                            className="mt-4 border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-gray-50 transition-colors group"
                            onClick={triggerFileInput}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                        >
                            <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-3 shadow-lg group-hover:scale-105 transition-transform">
                                <Upload className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-sm font-bold text-gray-900 mb-1">Drop your file here or click to browse</h3>
                            <p className="text-xs text-gray-400">Accepted formats: PDF, DOC, DOCX, PPT, PPTX, PNG, JPG</p>
                        </div>
                    )}
                </div>

                {/* Right Content - Chat Interface */}
                <div className="lg:col-span-8 flex flex-col gap-6 h-full overflow-hidden">

                    {/* Assistant Header Card */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center justify-between shadow-sm shrink-0">
                        <div className="flex items-center gap-3">
                            <img src={artieImage} alt="Artie" className="w-10 h-10 rounded-full object-contain bg-yellow-50" />
                            <div>
                                <h3 className="font-bold text-gray-900 text-sm">Work with Artie</h3>
                                <p className="text-xs text-gray-500">Your Strategy Assistant</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs font-medium text-gray-500 mb-1 flex items-center justify-end gap-1">
                                <span>🤖</span> Artie tokens utilized: <span className="text-gray-900 font-bold">{tokensUsed.toLocaleString()}</span> / {TOKEN_LIMIT.toLocaleString()}
                            </div>
                            <div className="w-48 h-1.5 bg-gray-100 rounded-full overflow-hidden ml-auto">
                                <div
                                    className={`h-full rounded-full transition-all duration-500 ease-out ${tokenPercentage > 90 ? "bg-red-600" : tokenPercentage > 70 ? "bg-amber-600" : "bg-blue-500"
                                        }`}
                                    style={{ width: `${Math.min(tokenPercentage, 100)}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>

                    {/* Chat Area */}
                    <div className="flex-1 bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden shadow-sm relative">
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">

                            {displayMessages.map((msg) => (
                                <div key={msg.id} className={`flex gap-4 max-w-3xl ${msg.sender === 'user' ? 'ml-auto justify-end' : ''}`}>
                                    {msg.sender === 'ai' && (
                                        <img src={artieImage} alt="Artie" className="w-8 h-8 rounded-full object-contain bg-yellow-50 flex-shrink-0 mt-1" />
                                    )}

                                    <div className={msg.sender === 'user' ? "w-auto" : "w-full"}>
                                        {msg.sender === 'user' ? (
                                            <div className="bg-[#1F1F1F] text-white rounded-2xl rounded-tr-sm px-6 py-4 text-sm leading-relaxed shadow-sm">
                                                {msg.content}
                                            </div>
                                        ) : (
                                            <div className="bg-transparent text-gray-800 text-sm leading-relaxed">
                                                {msg.content}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {isLoadingChat && (
                                <div className="flex gap-4 max-w-3xl">
                                    <img src={artieImage} alt="Artie" className="w-8 h-8 rounded-full object-contain bg-yellow-50 flex-shrink-0 mt-1" />
                                    <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-6 py-4 flex items-center">
                                        <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                                    </div>
                                </div>
                            )}

                            {chatError && (
                                <div className="flex justify-center my-4">
                                    <div className="bg-red-50 text-red-600 text-sm px-4 py-2 rounded-full border border-red-100 flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" />
                                        {chatError}
                                    </div>
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <div className="p-4 border-t border-gray-100">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Ask Artie for guidance, approaches, or feedback..."
                                    className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-full pl-6 pr-12 py-4 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all placeholder:text-gray-400 disabled:opacity-50"
                                    disabled={isLoadingChat}
                                />
                                <button
                                    onClick={() => handleSendMessage()}
                                    disabled={isLoadingChat || !inputValue.trim()}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ArrowUp className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </main>
        </div>
    );
};

export default ChatbotPage;